import { useState, useEffect, useContext, useRef } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './text-editor-override.css';
import _ from 'lodash';
import striptags from 'striptags'
import { PulseLoader } from "react-spinners";

import { StateContext } from "../services/context";
import { classNames } from "../utils/utils";
import { api } from "../services/POST";
import { FaArrowRotateLeft, FaArrowRotateRight } from "react-icons/fa6";
import { RiRobot2Line } from "react-icons/ri";
import { RxTextAlignBottom } from "react-icons/rx";
import { DocumentData } from "../utils/state";
import { ImportIcon, SaveIcon, DeleteIcon, ChangeIcon } from "../utils/iconFactory";

import { LoadDocumentModal } from "./Modals/Documents/LoadDocumentModal";
import { DeleteDocumentModal } from "./Modals/Documents/DeleteDocumentModal";
import { NewDocumentModal } from "./Modals/Documents/NewDocumentModal";
import { ToEditorButton } from "./Elements/CustomButtons";
import { sendOpenAIPOST } from "../services/POST";

import SelectDocumentModal from "./Modals/Documents/SelectDocumentModal";
import Tooltip from "./Elements/Tooltip";
import { formats, modules, QuillToolbar } from "./QuillEditor";


export const TextEditor = (({generated_text}: {generated_text: any}) => {
  const {loadedDocument, setLoadedDocument, showPopup} = useContext(StateContext)
  const [text, setText] = useState(loadedDocument.data);
  const [documentName, setDocumentName] = useState<string>(loadedDocument.name);

  // Modals
  const [isLoadDocumentModalOpen, setIsLoadDocumentModalOpen] = useState(false);
  const [isDocumentsModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isNewDocumentModalOpen, setIsNewDocumentModalOpen] = useState(false);
  const [isDeletDocumentModalOpen, setIsDeletDocumentModalOpen] = useState(false);


  // Document history
  const [loadedDocuments, setLoadedDocuments] = useState<DocumentData[]>([])

  async function SaveDocument() {
    try {
      if (loadedDocument.id === '') {
        const data = await api("/save_document", {name: documentName, data: text})
        setLoadedDocument({id: data.id, name: data.name, data: data.data, date: data.date})
        showPopup("Sikeresen elmentetted a dokumentumot!", "success")
      } else {
        const data = await api("/save_document", {id: loadedDocument.id, name: documentName, data: text})
        setLoadedDocument({id: data.id, name: data.name, data: data.data, date: data.date})
        showPopup("Sikeresen elmentetted a változtatésokat!", "success")
      }
    } catch (err: any) {
      console.log(err)
    }
  }
  function isSaveButtonDisabled() {
    if (loadedDocument?.name === documentName && loadedDocument?.data === text || documentName === "" || text === "") return true
    return false
  }

  async function LoadDocuments() {
    try {
      const data = await api("/get_documents")
      setLoadedDocuments(data)
    } catch (err: any) {
      console.log(err)
    }
  }
  
  function handleChangeDocument(id: string) {
    for (let item of loadedDocuments){
      if (item.id === id) {
        setLoadedDocument({id: item.id, name: item.name, data: item.data, date: item.date});
        setActualText(item.data);
        setDocumentName(item.name);
        setIsDocumentModalOpen(false);
        showPopup("Sikeresen betöltötted a dokumentumot!", "success");
      }
    }
  }
  
  function newDocument() {
    setLoadedDocument({id: '', name: '', data: '', date: ''})
    setActualText("")
    setDocumentName("")
  }
  
  const deleteDocument = async() => {
    await api("/delete_document", {id: loadedDocument.id})
    await LoadDocuments();
    newDocument();
    showPopup("Sikeresen törölted a dokumentumot!", "success")
  };

  // CUSTOM UNDO/REDO

  const [history, setHistory] = useState<any[]>([]); // Dokumentum előzményei
  const [historyIndex, setHistoryIndex] = useState(-1); // Jelenlegi hely az előzményekben

  function setActualText(actualText: any, enableHistory: boolean = true) {
    setText(actualText)
    if (enableHistory) {
      const newHistory = history.slice(0, historyIndex + 1);
      newHistory.push(actualText);
      setHistory(newHistory);
      setHistoryIndex(newHistory.length - 1);
    }
  }

  function goToPreviousState() {
    if (historyIndex > 0) {
      setActualText(history[historyIndex - 1], false);
      setHistoryIndex(historyIndex - 1);
    }
  }

  function goToNextState() {
    if (historyIndex < history.length - 1) {
      setActualText(history[historyIndex + 1], false);
      setHistoryIndex(historyIndex + 1);
    }
  }


  function addText() {
    const lines = generated_text.split('\n');
    for (let i = 0; i < lines.length; i++) {
      setActualText((text: any) => text + "<p>" + lines[i] + "</p>")
    }
    setTimeout(() => {scrollToBottom()}, 10);
  }

  function addContinueText(output_text: any) {
    const lines = output_text.split('\n');
    for (let i = 0; i < lines.length; i++) {
      setActualText((text: any) => text + "<p>" + lines[i] + "</p>")
    }
    setTimeout(() => {scrollToBottom()}, 10);
  }

  // CONTINUE GENERATION

  const [isContinueGenerationActive, setIsContinueGenerationActive] = useState(false)
  const continueGenerationStop = useRef(false)
  async function continueGeneration() {
    try {
      setIsContinueGenerationActive(true);
      const data = await api('/getPromptForContinue', {inputText: striptags(text)})
      await sendOpenAI(data.prompt, data.temp, data.key)
    } catch (err: any) {
      console.log(err)
    }
  }

  async function sendOpenAI(prompt: any, temp: any, key: any) {
    const { response, controller } = await sendOpenAIPOST(prompt, temp, key);
    const reader = response.body?.getReader();
    continueGenerationStop.current = false;
    if (reader) {
      try {
        let accumulatedContent = "";
        while (!controller.signal.aborted) {
          const { done, value } = await reader.read();
          if (done || continueGenerationStop.current) break;
          for (const line of new TextDecoder("utf-8").decode(value).split("\n")) {
            try {
              const trimmedLine = line.trim();
              if (trimmedLine) {
                const obj = JSON.parse(trimmedLine.replace("data: ", ""));
                const content = _.toString(_.get(obj, "choices.0.delta.content"));
                accumulatedContent += content;
              }
            } catch (err) {
              console.log("Error:", err);
            }
          }
        }
        addContinueText(accumulatedContent)
      } catch (err) {
        console.log("Error in reading loop:", err);
      }
    }
    setIsContinueGenerationActive(false);
  }

  const [editorHeight, setEditorHeight] = useState('500px');
  useEffect(() => {
    const handleResize = () => {setEditorHeight(`${window.innerHeight - 480}px`)};
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  const quillRef = useRef<ReactQuill>(null)
  function scrollToBottom() {
    if (quillRef.current && quillRef.current.editor) {
      const editor = quillRef.current.editor;
      const range = editor.getLength();
      editor.setSelection(range, 0);
    }
  }

  return (
    <div className="flex flex-1 flex-col col-span-1 bg-backgroundWhite p-10">
      <ToEditorButton onClick={() => addText()}/>
      <div id='topic-select-flexbox' className="flex flex-col gap-6 pb-6">
        <h1 className="text-textMain text-style-h3">Szerkeszd kedvedre a generált szöveget</h1>
        <div className="flex flex-col gap-1">

          <div className="flex items-center">
            <p className="text-textMain text-style-body2 mr-4">Név:</p>
            <input type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              className={classNames("h-12 w-full px-4 py-2 rounded-[12px] bg-backgroundWhite",
                "border border-gray100 text-style-body2 text-textMain",
                "placeholder-gray100",
                "hover:border-primaryActionAltHover",
                "active:border-textMain active:ring-0 active:outline-none focus:border-textMain focus:ring-0 focus:outline-none",
                "disabled:bg-gray0 disabled:border-gray10 disabled:gray40")} 
              placeholder="Nevezd el a dokumentumot"
            />
          </div>
          <div className="flex items-center">
            <p className="invisible text-textMain text-style-body2 mr-4">Név:</p>
            <p className="flex px-2 py-3 text-primaryAction text-style-body2-bold cursor-pointer" 
              onClick={() => {if (isSaveButtonDisabled()) {{setIsDocumentModalOpen(true); LoadDocuments();}} else {setIsLoadDocumentModalOpen(true);}}}>
              <ImportIcon className="h-6 w-6 mr-2 fill-current" />
              Korábbi dokumentum betöltése és szerkesztése
            </p>
            <LoadDocumentModal isVisible={isLoadDocumentModalOpen} onClose={() => setIsLoadDocumentModalOpen(false)} execute={() => {setIsDocumentModalOpen(true); LoadDocuments()}}/>
            <SelectDocumentModal isVisible={isDocumentsModalOpen} onClose={() => setIsDocumentModalOpen(false)} loadedDocuments={loadedDocuments} handleChangeDocument={handleChangeDocument}/>
          </div>
        </div>
      </div>


      <div className="flex-1 border border-gray100 rounded-[12px] overflow-hidden">
        <div className="flex justify-between border-b border-gray0">
          <div className="flex flex-block bg-backgroundWhite">
            <QuillToolbar />
            <button className="mr-2" onClick={() => goToPreviousState()}>
              {<FaArrowRotateLeft/>}
            </button>
            <button className="mr-2" onClick={() => goToNextState()}>
              {<FaArrowRotateRight/>}
            </button>
          </div>
          <button className="flex flex-block pt-1.5 pr-4" onClick={() => {isContinueGenerationActive ? continueGenerationStop.current = true : continueGeneration()}}>
            {isContinueGenerationActive ? <>{<h1 className="pt-1">Stop</h1>}{<PulseLoader size={8} className="ml-1 pt-2.5"/>}</> : <><RxTextAlignBottom className="pt-0.5" size={25}/><RiRobot2Line size={12}/></>}
          </button>
        </div>
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={text}
          onChange={(e) => {setActualText(e)}}
          modules={modules}
          formats={formats}
          placeholder="Itt szerkesztheted a generált tartalmat..."
          style={{ height: editorHeight, minHeight: '210px', overflowY: 'auto'}}
        />
      </div>
      <div className="flex justify-between pt-8">
        <div className="flex flex-row gap-4">
          <button
            onClick={SaveDocument}
            className={classNames("flex px-4 py-3 rounded-[12px] text-style-body2-bold text-backgroundWhite",
            isSaveButtonDisabled() ? "bg-gray40" : "bg-primaryAction",
              "transform transform-all duration-300 active:scale-95")}
            disabled={isSaveButtonDisabled()}
          >
            <SaveIcon className="h-6 w-6 mr-2 fill-current" />
            {loadedDocument?.id !== '' ? "Változtatások mentése" : "Dokumentum mentés"}
          </button>
          <div className="group relative cursor-pointer">
            <button className="flex px-4 py-3 rounded-[12px] text-style-body2-bold text-gray50"
              onClick={() => setIsDeletDocumentModalOpen(true)} 
              disabled={loadedDocument.id === ''}
            >
              <Tooltip message={
                  loadedDocument.id === '' ? "Először mentsd el a dokumentumot!" : 
                  "Dokumentum törlése esetén a dokumentumodat véglegesen töröljük, a továbbiakban már nem tudod visszaállítani."
                } 
              />
              <DeleteIcon className="h-6 w-6 mr-2 fill-current" />
              Dokumentum törlése
            </button>
          </div>
          <DeleteDocumentModal isVisible={isDeletDocumentModalOpen} onClose={() => setIsDeletDocumentModalOpen(false)} execute={() => deleteDocument()}/>
        </div>
        <button
          onClick={() => {if (isSaveButtonDisabled()) {newDocument(); showPopup("Sikeresen létrehoztál egy új dokumentumot!", "success")} else {setIsNewDocumentModalOpen(true)}}}
          // {if (isSaveButtonDisabled()) {{setIsDocumentModalOpen(true); LoadDocuments();}} else {setIsLoadDocumentModalOpen(true);}}}></div>
          className={classNames("flex px-4 py-3 rounded-[12px] text-style-body2-bold text-primaryAction",
            "transform transform-all duration-300 active:scale-95")}
        >
          <ChangeIcon className="h-6 w-6 mr-2 fill-current" />
          Új létrehozása
        </button>
        
        <NewDocumentModal isVisible={isNewDocumentModalOpen} onClose={() => setIsNewDocumentModalOpen(false)} execute={() => {newDocument(); showPopup("Sikeresen létrehoztál egy új dokumentumot!", "success")}}/>
      </div>
    </div>
  );
});